import { useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { UserPen, X, AlertCircle, Loader2, Upload, Link2, Users } from "lucide-react";
import { api, type GuestPostInput, type AuthorRecordView } from "../../lib/api.js";
import { RichTextEditor } from "../../components/RichTextEditor.js";

/**
 * Guest post — "bring your own body + author bio". The operator provides the
 * target keyword, title, article body (markdown), and a byline author (name +
 * bio). The system assembles it into an Article (no section-writing spend),
 * generates the SEO meta from the body, resolves internal links, and lands it in
 * the pipeline at "reviewing" — ready for Images + Publish. The guest author's
 * Sanity doc is created at publish so their name + bio render on the live site.
 */
export function GuestPostDialog({
  onCancel,
  onCreated,
}: {
  onCancel: () => void;
  onCreated: (keywordId: string) => void;
}) {
  const [keyword, setKeyword] = useState("");
  const [title, setTitle] = useState("");
  const [bodyMarkdown, setBody] = useState("");
  const [language, setLanguage] = useState<"he" | "en">("he");
  const [nameHe, setNameHe] = useState("");
  const [nameEn, setNameEn] = useState("");
  const [slug, setSlug] = useState("");
  const [bioHe, setBioHe] = useState("");
  const [credentialsHe, setCredentials] = useState("");
  const [profileUrl, setProfileUrl] = useState("");
  const [avatar, setAvatar] = useState({ url: "", assetRef: "" });
  const [hero, setHero] = useState({ url: "", assetRef: "" });
  const [error, setError] = useState<string | null>(null);
  const [weaveResult, setWeaveResult] = useState<string | null>(null);

  // Byline author: pick an EXISTING author (reuses their Sanity doc + slug) or
  // create a new one. Defaults to "existing" when the registry has any.
  const [authorMode, setAuthorMode] = useState<"existing" | "new">("existing");
  const [existingId, setExistingId] = useState("");
  const authorsQ = useQuery({ queryKey: ["authors"], queryFn: () => api.listAuthors() });
  const authors = authorsQ.data?.authors ?? [];
  const mode: "existing" | "new" = authors.length === 0 ? "new" : authorMode;
  // Select by the Sanity doc id when present (globally unique — the local id/slug
  // can collide across house authors); the route resolves it back to the author.
  const selectedId = existingId || (authors[0] ? authorKey(authors[0]) : "");

  const weave = useMutation({
    mutationFn: () =>
      api.weaveLinks({ keyword: keyword.trim(), bodyMarkdown, language, ...(title.trim() ? { title: title.trim() } : {}) }),
    onSuccess: (r) => {
      if (!r.ok) { setError(r.error ?? "Run links failed"); return; }
      setBody(r.bodyMarkdown);
      setWeaveResult(r.summary);
    },
    onError: (e: Error) => setError(e.message),
  });

  const create = useMutation({
    mutationFn: () => {
      const author: GuestPostInput["author"] = mode === "existing"
        ? { id: selectedId }
        : {
            nameHe: nameHe.trim(),
            ...(nameEn.trim() ? { nameEn: nameEn.trim() } : {}),
            ...(slug.trim() ? { slug: slug.trim() } : {}),
            bioHe: bioHe.trim(),
            ...(credentialsHe.trim() ? { credentialsHe: credentialsHe.trim() } : {}),
            ...(profileUrl.trim() ? { profileUrl: profileUrl.trim() } : {}),
            ...(avatar.url.trim() ? { avatarUrl: avatar.url.trim() } : {}),
            ...(avatar.assetRef ? { avatarAssetRef: avatar.assetRef } : {}),
          };
      return api.createGuestPost({
        keyword: keyword.trim(), title: title.trim(), bodyMarkdown, language, author,
        ...(hero.assetRef ? { heroImageAssetRef: hero.assetRef } : {}),
        ...(hero.url.trim() ? { heroImageUrl: hero.url.trim() } : {}),
      });
    },
    onSuccess: (r) => {
      if (!r.ok || !r.keywordId) { setError(r.error ?? "create failed"); return; }
      onCreated(r.keywordId);
    },
    onError: (e: Error) => setError(e.message),
  });

  function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    if (!keyword.trim() || !title.trim()) return setError("Target keyword and title are required.");
    if (bodyMarkdown.trim().length < 30) return setError("Provide the article body (markdown).");
    if (mode === "existing") {
      if (!selectedId) return setError("Select an author, or switch to New to add one.");
    } else if (!nameHe.trim() || bioHe.trim().length < 10) {
      return setError("Author name and a short bio are required.");
    }
    create.mutate();
  }

  return (
    <>
      <div className="fixed inset-0 z-[60] bg-ink/30 backdrop-blur-[2px]" onClick={onCancel} aria-hidden />
      <form onSubmit={submit} className="fixed left-1/2 top-1/2 z-[61] w-full max-w-2xl -translate-x-1/2 -translate-y-1/2 rounded-2xl bg-white p-6 shadow-2xl">
        <div className="mb-3 flex items-start justify-between">
          <div>
            <div className="flex items-center gap-2 text-2xs uppercase tracking-wider text-brand-700"><UserPen className="h-3.5 w-3.5" /> Guest post</div>
            <h3 className="mt-1 text-lg font-semibold text-ink">Bring your own body + author</h3>
            <p className="mt-1 text-xs text-muted text-balance">You provide the article + the author bio; the system generates the SEO meta, resolves internal links, and (after you add images) publishes it under the guest byline.</p>
          </div>
          <button type="button" onClick={onCancel} className="btn-ghost shrink-0"><X className="h-4 w-4" /></button>
        </div>

        <div className="max-h-[65vh] space-y-3 overflow-y-auto pr-1">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label">Target keyword <span className="text-danger">*</span></label>
              <input className="input" dir="auto" value={keyword} onChange={(e) => setKeyword(e.target.value)} placeholder="e.g. רפלקסולוגיה לחרדה" autoFocus />
            </div>
            <div>
              <label className="label">Language</label>
              <select className="input" value={language} onChange={(e) => setLanguage(e.target.value as "he" | "en")}>
                <option value="he">Hebrew</option>
                <option value="en">English</option>
              </select>
            </div>
          </div>
          <div>
            <label className="label">Title (H1) <span className="text-danger">*</span></label>
            <input className="input" dir="auto" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="The article headline" />
          </div>
          <div>
            <div className="flex items-center justify-between gap-2">
              <label className="label">Article body <span className="text-danger">*</span></label>
              <button
                type="button"
                onClick={() => { setError(null); weave.mutate(); }}
                disabled={weave.isPending || !keyword.trim() || bodyMarkdown.trim().length < 30}
                className="btn-ghost btn-sm disabled:opacity-50"
                title="Insert our must-have links + related internal links into the body. The result lands here for you to review/edit before submitting."
              >
                {weave.isPending ? (
                  <><Loader2 className="h-3.5 w-3.5 animate-spin" /> Running links…</>
                ) : (
                  <><Link2 className="h-3.5 w-3.5" /> Run links</>
                )}
              </button>
            </div>
            <RichTextEditor
              value={bodyMarkdown}
              onChange={(md) => { setBody(md); if (weaveResult) setWeaveResult(null); }}
              placeholder="Write or paste the article. Use the toolbar for headings, lists, and links."
              onUploadImage={async (dataUrl) => {
                const r = await api.uploadImage(dataUrl);
                if (!r.ok || !r.url) { setError(r.error ?? "Image upload failed"); return null; }
                return r.url;
              }}
            />
            {weaveResult && <p className="mt-1 text-2xs text-emerald-700">{weaveResult}</p>}
            <p className="mt-1 text-2xs text-muted">
              Use the image button to mark where an image should be generated. A leading H1 is ignored (use the Title field).
              <strong> Run links</strong> needs the Target keyword filled in.
            </p>
          </div>

          <ImageField
            label="Hero image (optional)"
            hint="Becomes the article's main image (mainImage). Paste a URL or upload a file — stored in Sanity."
            value={hero}
            onChange={setHero}
            onError={setError}
          />

          <div className="rounded-lg border border-line p-3">
            <div className="mb-2 flex items-center justify-between gap-2">
              <div className="text-2xs uppercase tracking-wider text-muted">Byline author</div>
              {authors.length > 0 && (
                <div className="flex rounded-md border border-line p-0.5 text-2xs">
                  <button type="button" onClick={() => setAuthorMode("existing")} className={`rounded px-2 py-0.5 ${mode === "existing" ? "bg-brand-50 font-medium text-brand-700" : "text-muted"}`}>Existing</button>
                  <button type="button" onClick={() => setAuthorMode("new")} className={`rounded px-2 py-0.5 ${mode === "new" ? "bg-brand-50 font-medium text-brand-700" : "text-muted"}`}>New</button>
                </div>
              )}
            </div>

            {mode === "existing" ? (
              <ExistingAuthorPicker
                authors={authors}
                loading={authorsQ.isLoading}
                selectedId={selectedId}
                onSelect={setExistingId}
              />
            ) : (
            <>
            <p className="mb-2 text-2xs text-muted">Creates the Sanity author document — no need to visit Studio. It'll then be reusable here.</p>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="label">Name <span className="text-danger">*</span></label>
                <input className="input" dir="auto" value={nameHe} onChange={(e) => setNameHe(e.target.value)} placeholder={'ד"ר ישראל ישראלי'} />
              </div>
              <div>
                <label className="label">Display name</label>
                <input className="input" dir="auto" value={nameEn} onChange={(e) => setNameEn(e.target.value)} placeholder="public display name, if different" />
              </div>
            </div>
            <div className="mt-2">
              <label className="label">Bio <span className="text-danger">*</span></label>
              <textarea className="input" dir="auto" rows={3} value={bioHe} onChange={(e) => setBioHe(e.target.value)} placeholder="פסקה קצרה על המחבר/ת — הכשרה, ניסיון, תחום התמחות. (rich text — markdown supported)" />
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <div>
                <label className="label">Website URL</label>
                <input className="input" value={profileUrl} onChange={(e) => setProfileUrl(e.target.value)} placeholder="https://… (profile or personal site)" />
              </div>
              <div>
                <label className="label">Slug</label>
                <input className="input font-mono text-xs" value={slug} onChange={(e) => setSlug(e.target.value)} placeholder="auto from name" />
              </div>
            </div>
            <div className="mt-2 grid grid-cols-2 gap-3">
              <ImageField label="Profile image" hint="Paste a URL or upload a file — stored in Sanity." value={avatar} onChange={setAvatar} onError={setError} round />
              <div>
                <label className="label">Credentials <span className="text-2xs font-normal text-muted">(byline)</span></label>
                <input className="input" dir="auto" value={credentialsHe} onChange={(e) => setCredentials(e.target.value)} placeholder="נטורופת מוסמך, 12 שנות ניסיון" />
              </div>
            </div>
            </>
            )}
          </div>
        </div>

        {error && (
          <div className="mt-3 flex items-start gap-2 rounded-lg bg-danger-soft px-3 py-2 text-xs text-red-700">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" /><span>{error}</span>
          </div>
        )}

        <footer className="mt-4 flex items-center justify-end gap-2">
          <button type="button" onClick={onCancel} className="btn-secondary btn-sm" disabled={create.isPending}><X className="h-3.5 w-3.5" /> Cancel</button>
          <button type="submit" className="btn-primary btn-sm" disabled={create.isPending}>
            {create.isPending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <UserPen className="h-3.5 w-3.5" />} Create guest post
          </button>
        </footer>
      </form>
    </>
  );
}

/** Stable selection key — the Sanity doc id when present (unique), else the local id. */
function authorKey(a: AuthorRecordView): string {
  return a.sanityRef || a.id;
}

/** Pick an existing byline author (local registry ∪ Sanity author docs) + preview it. */
function ExistingAuthorPicker({ authors, loading, selectedId, onSelect }: {
  authors: AuthorRecordView[];
  loading: boolean;
  selectedId: string;
  onSelect: (id: string) => void;
}) {
  const sel = authors.find((a) => authorKey(a) === selectedId) ?? null;
  return (
    <div>
      <p className="mb-2 text-2xs text-muted">Reuses this author's existing Sanity document, slug, bio + photo — nothing to re-enter.</p>
      <div className="flex items-center gap-2">
        <Users className="h-4 w-4 shrink-0 text-muted" />
        <select className="input flex-1" value={selectedId} onChange={(e) => onSelect(e.target.value)} disabled={loading}>
          {loading && <option>Loading authors…</option>}
          {!loading && authors.map((a) => (
            <option key={authorKey(a)} value={authorKey(a)}>{a.nameHe}{a.nameEn && a.nameEn !== a.nameHe ? ` — ${a.nameEn}` : ""}</option>
          ))}
        </select>
      </div>
      {sel && (
        <div className="mt-2 flex items-start gap-3 rounded-md border border-line bg-canvas p-2">
          {sel.avatarUrl
            ? <img src={sel.avatarUrl} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-line" onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
            : <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-line text-muted"><UserPen className="h-4 w-4" /></div>}
          <div className="min-w-0">
            <div className="truncate text-xs font-medium text-ink" dir="auto">{sel.nameHe}</div>
            {sel.credentialsHe && <div className="truncate text-2xs text-muted" dir="auto">{sel.credentialsHe}</div>}
            {sel.bioHe && <p className="mt-0.5 line-clamp-2 text-2xs text-muted" dir="auto">{sel.bioHe}</p>}
          </div>
        </div>
      )}
    </div>
  );
}

/** Image input that accepts a URL OR a file upload (→ Sanity). Used for the author photo + the hero. */
function ImageField({ label, hint, value, onChange, onError, round }: {
  label: string;
  hint?: string;
  value: { url: string; assetRef: string };
  onChange: (v: { url: string; assetRef: string }) => void;
  onError: (msg: string | null) => void;
  round?: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  async function onPick(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    onError(null);
    if (!file.type.startsWith("image/")) return onError("Please choose an image file.");
    if (file.size > 8 * 1024 * 1024) return onError("Image too large (max 8MB).");
    setUploading(true);
    try {
      const dataUrl: string = await new Promise((resolve, reject) => {
        const fr = new FileReader();
        fr.onload = () => resolve(String(fr.result));
        fr.onerror = () => reject(new Error("Could not read the file."));
        fr.readAsDataURL(file);
      });
      const r = await api.uploadImage(dataUrl);
      if (!r.ok || !r.assetRef || !r.url) throw new Error(r.error ?? "upload failed");
      onChange({ url: r.url, assetRef: r.assetRef });
    } catch (err) {
      onError(`Image upload failed: ${(err as Error).message}`);
    } finally {
      setUploading(false);
    }
  }
  return (
    <div>
      <label className="label">{label}</label>
      <div className="flex items-center gap-2">
        <input className="input flex-1" value={value.url} onChange={(e) => onChange({ url: e.target.value, assetRef: "" })} placeholder="https://… image URL" />
        <label className={`btn-secondary btn-sm shrink-0 cursor-pointer ${uploading ? "pointer-events-none opacity-60" : ""}`} title="Upload a file instead of a URL">
          {uploading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />} Upload
          <input type="file" accept="image/*" className="hidden" onChange={onPick} disabled={uploading} />
        </label>
      </div>
      {hint && <p className="mt-1 text-2xs text-muted">{hint}</p>}
      {value.url.trim() && (
        <div className="mt-2 flex items-center gap-2">
          <img src={value.url} alt="" className={`${round ? "h-10 w-10 rounded-full" : "h-14 w-24 rounded"} object-cover ring-1 ring-line`} onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }} />
          <span className="text-2xs text-muted">{value.assetRef ? "Uploaded to Sanity ✓" : "Preview"}</span>
        </div>
      )}
    </div>
  );
}
